import React from 'react';

import { UserRole } from 'core/constants';

import API from 'utils/api';
import { UserFieldDropdownKind, UserFieldGridRow, UserFieldOption } from 'utils/api/modules/user/user.types';
import { UnitResponse } from 'utils/api/modules/units/units.types';
import {
  EMPTY_IMPORT_VOCABULARY,
  ImportCustomFieldColumn,
  ImportUnitOption,
  ImportVocabulary,
} from 'utils/csv/importVocabulary';
import { useStore } from 'utils/hooks/store';

type DropdownLabels = Pick<ImportVocabulary, 'departmentLabel' | 'regionLabel' | 'employmentTypeLabel'>;

const DROPDOWN_LABEL_KEYS: Record<string, keyof DropdownLabels> = {
  department: 'departmentLabel',
  region: 'regionLabel',
  'employment-type': 'employmentTypeLabel',
};

const fromGrid = (rows: UserFieldGridRow[]): DropdownLabels & { customFields: ImportCustomFieldColumn[] } => {
  const labels: DropdownLabels & { customFields: ImportCustomFieldColumn[] } = {
    departmentLabel: EMPTY_IMPORT_VOCABULARY.departmentLabel,
    regionLabel: EMPTY_IMPORT_VOCABULARY.regionLabel,
    employmentTypeLabel: EMPTY_IMPORT_VOCABULARY.employmentTypeLabel,
    customFields: [],
  };

  rows.forEach((row) => {
    if (!row.isActive) {
      return;
    }
    if (row.kind === 'dropdown') {
      const key = DROPDOWN_LABEL_KEYS[row.key];
      if (key) {
        labels[key] = row.label;
      }
      return;
    }
    labels.customFields.push({ slot: row.slot, label: row.label });
  });

  return labels;
};

const unitOptions = (units: UnitResponse[]): ImportUnitOption[] => {
  const byUuid = new Map(units.map((unit) => [unit.uuid, unit]));
  const path = (unit: UnitResponse): string[] => {
    const segments = [unit.name];
    for (let parent = unit.parent; parent; parent = byUuid.get(parent)?.parent ?? null) {
      segments.unshift(byUuid.get(parent)?.name ?? '');
    }
    return segments;
  };

  return units
    .filter((unit) => unit.isActive && unit.kind !== 'intake')
    .map((unit) => ({ uuid: unit.uuid, path: path(unit) }));
};

export interface UseImportVocabulary {
  vocabulary: ImportVocabulary;
  loading: boolean;
  failed: boolean;
  retry: () => void;
}

export const useImportVocabulary = (): UseImportVocabulary => {
  const { session } = useStore();
  const [vocabulary, setVocabulary] = React.useState<ImportVocabulary>(EMPTY_IMPORT_VOCABULARY);
  const [loading, setLoading] = React.useState(true);
  const [failed, setFailed] = React.useState(false);
  const [attempt, setAttempt] = React.useState(0);

  const showObjectManager = !!session.user?.hasObjectManager;
  const showSite = (session.user?.organisation?.maximumSitesAllowed ?? 0) > 0;
  const showUnit = !!session.user?.scopedPermissions;
  // Every read below is session-scoped, so on a staff cross-org import it would fetch the wrong org.
  const suppressed = session.user?.role === UserRole.Admin;

  React.useEffect(() => {
    if (suppressed) {
      setVocabulary({ ...EMPTY_IMPORT_VOCABULARY, suppressed: true });
      setLoading(false);
      setFailed(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setFailed(false);

    const kinds: UserFieldDropdownKind[] = ['department', 'employment-type'];
    if (showObjectManager) {
      kinds.push('region');
    }

    Promise.all([
      API.user.loadUserFieldsGrid()(),
      Promise.all(kinds.map((kind) => API.user.listUserFieldOptions(kind)())),
      showUnit ? API.units.loadUnits()() : Promise.resolve(null),
    ])
      .then(([gridResponse, optionResponses, unitResponse]) => {
        if (!active) {
          return;
        }
        const options: Record<UserFieldDropdownKind, UserFieldOption[]> = {
          department: [],
          'employment-type': [],
          region: [],
        };
        kinds.forEach((kind, index) => {
          options[kind] = optionResponses[index].data;
        });

        setVocabulary({
          ...fromGrid(gridResponse.data),
          departmentOptions: options.department,
          employmentTypeOptions: options['employment-type'],
          regionOptions: options.region,
          showObjectManager,
          showSite,
          showUnit,
          unitOptions: unitOptions(unitResponse?.data ?? []),
          suppressed: false,
        });
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [showObjectManager, showSite, showUnit, suppressed, attempt]);

  const retry = React.useCallback(() => setAttempt((previous) => previous + 1), []);

  return { vocabulary, loading, failed, retry };
};

export default useImportVocabulary;
